import React from 'react'
import qs from 'qs'
import { Toasts } from '../components/toaster/Toast'

interface Options {
    ignore?: boolean

    [key: string]: any
}

type ApiCall = (
    endpoint: string,
    params?: object | null,
    raw?: boolean | null,
    options?: Options,
) => Promise<any>

// ============================================================================
// ts_bulk_actions API Client
// ============================================================================

/**
 * Get the ts_bulk_actions base URL from meta tag or default to localhost
 */
function getTsBulkActionsBaseUrl(): string {
    const metaTag = document.querySelector('meta[name="ts-bulk-actions-url"]')
    return metaTag?.getAttribute('content') || 'http://127.0.0.1:3000'
}

/**
 * POST request to ts_bulk_actions service
 */
export const tsBulkActionsPost = (
    endpoint: string,
    params: object | null = null,
    options?: Options,
): Promise<any> => {
    const baseUrl = getTsBulkActionsBaseUrl()
    return handle(
        window.axios.post(`${baseUrl}/${endpoint}`, params, {
            ...options,
            withCredentials: true, // Send session cookie
        }),
        options,
    )
}

/**
 * PATCH request to ts_bulk_actions service
 */
export const tsBulkActionsPatch = (
    endpoint: string,
    params: object | null = null,
    options?: Options,
): Promise<any> => {
    const baseUrl = getTsBulkActionsBaseUrl()
    return handle(
        window.axios.patch(`${baseUrl}/${endpoint}`, params, {
            ...options,
            withCredentials: true, // Send session cookie
        }),
        options,
    )
}

/**
 * GET request to ts_bulk_actions service
 */
export const tsBulkActionsGet = (
    endpoint: string,
    params: object | null = null,
    options?: Options,
): Promise<any> => {
    const baseUrl = getTsBulkActionsBaseUrl()
    return handle(
        window.axios.get(`${baseUrl}/${endpoint}${params ? '?' + qs.stringify(params) : ''}`, {
            ...options,
            withCredentials: true, // Send session cookie
        }),
        options,
    )
}

/**
 * DELETE request to ts_bulk_actions service
 */
export const tsBulkActionsDelete = (
    endpoint: string,
    params: object | null = null,
    options?: Options,
): Promise<any> => {
    const baseUrl = getTsBulkActionsBaseUrl()
    return handle(
        window.axios.delete(`${baseUrl}/${endpoint}${params ? '?' + qs.stringify(params) : ''}`, {
            ...options,
            withCredentials: true, // Send session cookie
        }),
        options,
    )
}

// ============================================================================
// Laravel API Client
// ============================================================================

export const axiosGet: ApiCall = (endpoint, params = null, raw = false, options): Promise<any> => {
    return handle(
        window.axios.get(
            `${raw ? '' : '/api/v1'}/${endpoint}${params ? `?${qs.stringify(params)}` : ''}`,
            options,
        ),
        options,
    )
}

export const axiosPost: ApiCall = (endpoint, params = null, raw = false, options): Promise<any> => {
    return handle(
        window.axios.post(`${raw ? '' : '/api/v1'}/${endpoint}`, params, options),
        options,
    )
}

export const axiosPostWithQuery = (
    endpoint: string,
    params = null,
    query = null,
    raw = false,
    options: Options | undefined,
): Promise<any> => {
    return handle(
        window.axios.post(
            `${raw ? '' : '/api/v1'}/${endpoint}${query ? '?' + qs.stringify(query) : ''}`,
            params,
            options,
        ),
        options,
    )
}

export const axiosPatch: ApiCall = (
    endpoint,
    params = null,
    raw = false,
    options,
): Promise<any> => {
    return handle(
        window.axios.patch(`${raw ? '' : '/api/v1'}/${endpoint}`, params, options),
        options,
    )
}

export const axiosDestroy: ApiCall = (
    endpoint,
    params = null,
    raw = false,
    options,
): Promise<any> => {
    return handle(
        window.axios.delete(
            `${raw ? '' : '/api/v1'}/${endpoint}${params ? '?' + qs.stringify(params) : ''}`,
            options,
        ),
        options,
    )
}

const ignoredMessages: { [key: string]: boolean } = {
    // We can add specific types of error messages we don't want to show here, such as
    Unauthorized: true,
}

const ignoredStatuses: { [key: number]: boolean } = {
    401: true,
    409: true,
}

const reloadablePromotionErrors = new Set([
    'promotion_backend_conflict',
    'promotion_entity_snapshot_incomplete',
])

export const promotionEntityError = (data: any): JSX.Element | null => {
    if (!reloadablePromotionErrors.has(data?.error?.code)) {
        return null
    }

    return (
        <div>
            <div>{data.error.message}</div>
            <button
                type="button"
                className="mt-2 underline"
                onClick={() => window.location.reload()}
            >
                Reload Smart Campaign
            </button>
        </div>
    )
}

const handle = (promise: Promise<any>, options: Options | undefined): Promise<any> =>
    promise
        .then((res) => {
            return res
        })
        .catch((res) => {
            console.log('Caught API error', res, res.response?.data, options)

            const data = res.response?.data ?? {}
            const promotionError = promotionEntityError(data)
            if (promotionError) {
                Toasts.error(promotionError, `promotion-${data.error.code}`)
                return Promise.reject(res)
            }

            let message: string | JSX.Element = 'Something went wrong'
            if (data.message && data.errors) {
                message = (
                    <div>
                        {data.message}
                        <ul className="mb-0 mt-2">
                            {Object.keys(data.errors).map((key) => {
                                return data.errors[key].map((m: any, i: any) => {
                                    return <li key={key + ':' + i}>{m}</li>
                                })
                            })}
                        </ul>
                    </div>
                )
            } else if (data.message) {
                message = data.message
            }

            const userIgnore =
                // Compare on the path only. Any query string on this request defeats an exact
                // match -- the retired ad-manager `backend` override did, and an unauthenticated
                // visit to a page carrying it popped the error toast this deliberately suppresses.
                (res.config?.url?.split('?')[0] === '/api/v1/user' ||
                    res.config?.url?.split('?')[0] === '/api/v5/user') &&
                res.config?.method === 'get' &&
                res.request?.status === 401
            const path = window.location.pathname
            const pathIgnore: string[] = [
                // '/login',
                // '/register',
            ]

            if (
                !ignore(message, res.response?.status, options) &&
                !userIgnore &&
                !pathIgnore.includes(path)
            ) {
                Toasts.error(message as string)
            }

            return Promise.reject(res)
        })

const ignore = (
    message: string | JSX.Element,
    status: number,
    options: Options | undefined,
): boolean => {
    let ignore = false

    if (typeof message === 'string') {
        if (ignoredMessages[message]) {
            ignore = true
        }
    }

    if (ignoredStatuses[status]) {
        ignore = true
    }

    if (options?.ignore) {
        ignore = true
    }

    return ignore
}
